import { useEffect, useState } from "react";

import { BID_OK, SSP_CURRENCY, fetchSspBid } from "../services/fetchSspBid";

export const WINNER_SSP = "ssp";
export const WINNER_RIVAL = "rival";

function bidCpm(bid) {
    const cpm = bid ? Number(bid.cpm) : NaN;

    return bid && bid.cpm !== null && bid.cpm !== "" && Number.isFinite(cpm) ? cpm : null;
}

/**
 * Исход аукциона по ответу `/yhb`: RTB выигрывает, только если его лучшая ставка в
 * рублях строго больше цены соперника. Всё остальное — нет ставки, ошибка, таймаут,
 * чужая валюта, ставка без cpm или displayCode, равенство — победа соперника.
 */
export function pickWinner(response, floorCpm) {
    if (!response || response.status !== BID_OK) {
        return { winner: WINNER_RIVAL, bid: null, reason: response ? response.status : "error" };
    }

    const bids = Array.isArray(response.bids) ? response.bids : [];
    if (!bids.length) {
        return { winner: WINNER_RIVAL, bid: null, reason: "nobid" };
    }

    const inCurrency = bids.filter((bid) => bid && bid.currency === SSP_CURRENCY);
    if (!inCurrency.length) {
        return { winner: WINNER_RIVAL, bid: null, reason: "currency" };
    }

    const usable = inCurrency.filter(
        (bid) => bidCpm(bid) !== null && typeof bid.displayCode === "string" && bid.displayCode
    );
    if (!usable.length) {
        return { winner: WINNER_RIVAL, bid: null, reason: "invalid" };
    }

    const best = usable.reduce((top, bid) => (bidCpm(bid) > bidCpm(top) ? bid : top));
    const cpm = bidCpm(best);

    if (cpm > floorCpm) {
        return { winner: WINNER_SSP, bid: best, reason: "bid" };
    }

    return { winner: WINNER_RIVAL, bid: best, reason: "floor" };
}

// Один аукцион на ключ за жизнь страницы: участники монтируются в разных местах
// дерева и в любом порядке, а запрос `/yhb` должен уйти ровно один.
const pending = new Map();
const settled = new Map();

function describe(auction, outcome) {
    const cpm = bidCpm(outcome.bid);
    const detail = cpm === null ? outcome.reason : `cpm=${cpm}`;

    const rival = auction.rivalName ? ` rival=${auction.rivalName}` : "";

    return `[ad-auction] ${auction.key}${rival} floor=${auction.floorCpm} ${detail} → ${outcome.winner}`;
}

export function runAuction(auction, { fetchBid = fetchSspBid, log = defaultLog } = {}) {
    if (pending.has(auction.key)) {
        return pending.get(auction.key);
    }

    const promise = fetchBid(auction.ssp).then(
        (response) => pickWinner(response, auction.floorCpm),
        () => pickWinner(null, auction.floorCpm)
    );

    const logged = promise.then((outcome) => {
        settled.set(auction.key, outcome);
        log(describe(auction, outcome));

        return outcome;
    });

    pending.set(auction.key, logged);

    return logged;
}

export function peekAuction(key) {
    return settled.has(key) ? settled.get(key) : null;
}

// Для тестов: мемоизация живёт в модуле и иначе переживает тест.
export function resetAuctions() {
    pending.clear();
    settled.clear();
}

function defaultLog(message) {
    if (typeof console !== "undefined") {
        console.info(message);
    }
}

/** Исход аукциона для компонента: null, пока аукцион идёт (или его нет), затем исход. */
export function useAuctionOutcome(auction) {
    const key = auction ? auction.key : null;
    const [outcome, setOutcome] = useState(() => (key ? peekAuction(key) : null));

    useEffect(() => {
        if (!auction) {
            return undefined;
        }

        let alive = true;
        runAuction(auction).then((result) => {
            if (alive) {
                setOutcome(result);
            }
        });

        return () => {
            alive = false;
        };
    }, [key]);

    return outcome;
}
